import * as React from 'react';
import { useMemo, useState } from 'react';
import { Popup } from '../popover/Popup';
import * as Classes from '../common/classes';
import { Icon } from '../icon/Icon';
import { IData } from '../virtual-data/VirtualData';
import {
  FinalTableComputedColumn,
  FinalTableProps,
  TblAggregateType,
} from './FinalTableProps';
import classNames from 'classnames';
import { getColumnFieldNoCheck } from './utils';
import { Menu } from '../menu/Menu';
import { MenuDivider } from '../menu/MenuDivider';
import { getIcon, getLabel, getMenuItem } from './aggregateTypeUtils';

export type Props<DATA extends IData<ID>, FILTER, ID> = {
  finalTableProps: FinalTableProps<DATA, FILTER, ID>;
  column: FinalTableComputedColumn<DATA>;
  isHovered?: boolean;
  setIsHover?: ((isHovered: boolean) => void) | undefined;
};

export const ColumnAggregate = <
  DATA extends IData<ID>,
  FILTER extends unknown,
  ID extends unknown
>({
  isHovered,
  column,
  finalTableProps,
  setIsHover,
}: Props<DATA, FILTER, ID>) => {
  const [isOpen, setOpen] = useState(false);

  if (column.type === 'action' || column.check) {
    return null;
  }

  let field = getColumnFieldNoCheck(column);
  let aggregate = field ? finalTableProps.getAggregate(field) : undefined;

  const setAggregate = (type?: TblAggregateType) => () => {
    setIsHover?.(false);
    return (
      field &&
      finalTableProps.setAggregate(
        field,
        type ? { field, aggregate: type } : undefined
      )
    );
  };

  const aggregateMenuItems = useMemo(() => {
    const items = [];

    if (field && (finalTableProps.isGrouping() || finalTableProps.showFooter)) {
      switch (column.type) {
        case 'date':
        case 'dateTime':
          items.push(getMenuItem(setAggregate, aggregate, undefined));
          items.push(getMenuItem(setAggregate, aggregate, 'min'));
          items.push(getMenuItem(setAggregate, aggregate, 'max'));
          break;
        case 'integer':
        case 'currency':
        case 'currencyDecimal':
        case 'decimal':
          items.push(getMenuItem(setAggregate, aggregate, undefined));
          items.push(getMenuItem(setAggregate, aggregate, 'avg'));
          items.push(getMenuItem(setAggregate, aggregate, 'sum'));
          items.push(getMenuItem(setAggregate, aggregate, 'min'));
          items.push(getMenuItem(setAggregate, aggregate, 'max'));
          break;
        default:
          break;
      }
    }

    return items;
  }, [field, finalTableProps, column.type, aggregate]);

  const isAggregated = useMemo<boolean>(
    () => ![undefined, 'none'].includes(aggregate?.aggregate),
    [aggregate?.aggregate]
  );

  return (
    <>
      {aggregateMenuItems.length > 0 &&
      (isAggregated || (!isAggregated && isHovered) || isOpen) ? (
        <Popup
          open={isOpen}
          onOpen={() => {
            setOpen(true);
          }}
          onClose={() => {
            setOpen(false);
          }}
          arrow
          mode={'view'}
          trigger={
            <div
              className={classNames(Classes.FINAL_TABLE_AGGREGATE_ICON_ACTION, {
                [Classes.FINAL_TABLE_AGGREGATE_TRANSPARENT]:
                  aggregate?.aggregate && aggregate?.aggregate !== 'none',
              })}
              title={getLabel(aggregate?.aggregate, true)}
            >
              <Icon icon={getIcon(aggregate?.aggregate)} fixedWidth />
            </div>
          }
        >
          {(closePopup: () => void) => {
            return (
              <div onClick={closePopup}>
                <Menu>
                  <MenuDivider title={'Agregační funkce'} />
                  {aggregateMenuItems}
                </Menu>
              </div>
            );
          }}
        </Popup>
      ) : null}
    </>
  );
};
